import { getURLParams } from '../adapters/dupes/queryParams.js';


/**
 * Map from NYC county names to our formatted county names
 */
export const NYCCountyNames = {
    Kings: 'Kings (Brooklyn)',
    Queens: 'Queens',
    'New York': 'New York (Manhattan)',
    Bronx: 'Bronx',
    Richmond: 'Richmond (Staten Island)',
};

/**
 * Map from borough names to our formatted county names
 */
export const boroughs = {
    Brooklyn: 'Kings (Brooklyn)',
    Queens: 'Queens',
    Manhattan: 'New York (Manhattan)',
    Bronx: 'Bronx',
    'Staten Island': 'Richmond (Staten Island)',
};

export const getLocationText = function (url) {
    var params = getURLParams(url);
    if (params.cityName) {
        return params.cityName + ', ' + params.state;
    } else if (params.countyName) {
        return countyDisplayName(params.countyName, params.state);
    }
    return params.state || params.locationName || '';
};

/**
 * Keep this logic in sync with SearchManagerImpl.countyDisplayName
 * @param {string} rawCountyName
 * @param {string} state
 * @return {string}
 */
var countyDisplayName = function (rawCountyName, state) {
    return rawCountyName.replace(/county$/, state === 'LA' ? ' Parish' : ' County') +
        ', ' + state;
};

export const locationParamNames = [
    'state',
    'cityName',
    'countyName',
    'centerLat',
    'centerLng',
    'locationName',
    'centerZip',
    'includeNearbyLocations',
    'community',
    'school',
];

/**
 * @returns {Object}
 */
export const getEmptyLocationParams = function () {
    var params = {};
    locationParamNames.forEach(function (name) {
        params[name] = null;
    });
    return params;
};

/**
 * @returns {Object}
 */
export const getBaseLocationParams = function () {
    var loc = getEmptyLocationParams();
    loc.includeNearbyLocations = true;
    return loc;
};

/**
 * @param {Object} maxMindLocation
 * @returns {string}
 */
export const makeSuggestionName = function (maxMindLocation) {
    var state = maxMindLocation.stateCode;
    var city = maxMindLocation.cityName;
    var county = maxMindLocation.countyName;

    if (city) {
        return city + ', ' + state;
    } else if (county) {
        return countyDisplayName(county, state);
    }
    return state;
};

/**
 * Converts a max mind location into a map of parameters suitable for a search URL
 *
 * TODO: we should consider what we really want to return if the max mind location
 * doesn't contain all the info we expect. Right now it should not error, but we
 * could end up with fewer filters than we wanted and thus broader location search.
 *
 * @param {!Object} maxMindLocation The detected location object from Max Mind
 * @returns {Object}
 */
export const extractSuggestionLocationParameters = function (maxMindLocation) {
    var stateCode = maxMindLocation.stateCode;
    var cityName = maxMindLocation.cityName;
    var borough = boroughs[cityName];
    var params = getBaseLocationParams();
    params.state = stateCode;

    if (stateCode === 'NY' && borough) {
        params.countyName = borough + 'county';
    } else {
        params.cityName = getAdjustedCityName(cityName, stateCode);
        params.countyName = maxMindLocation.countyName;
    }
    params.centerLat = maxMindLocation.latitude;
    params.centerLng = maxMindLocation.longitude;
    return params;
};

export const getAdjustedCityName = function (cityName, stateCode) {
    if (stateCode === 'NY') {
        if (cityName === 'New York') {
            return 'New York City';
        } else if (cityName === 'Cornwall-on-Hudson') {
            return 'Cornwall Hdsn';
        }
    }
    // We never use periods in our city names, but sometimes Google does
    // This is written to prevent errors on missing cityName, which occasionally happens
    return cityName && cityName.replace(/\./g, '');
};

/**
 * Extracts all the location-related parameters from a DC.org URL
 * @param {String} url
 * @returns {Object}
 */
export const extractURLLocationParameters = function (url) {
    var params = {};
    var oldParams = getURLParams(url);
    locationParamNames.forEach(function (name) {
        params[name] = oldParams[name];
    });
    return params;
};

/**
 * Used if the user wants to search for a specific location only
 * @param {string} url search URL
 * @returns {Object} Minimal parameters to search for the location without expansion
 */
export const removeLocationExpansionParams = function (url) {
    var params = getURLParams(url);
    var newParams = getEmptyLocationParams();

    if (params.centerZip) {
        newParams.centerZip = params.centerZip;
        // If zipOnly isn't set, we'll end up sorting all projects by proximity to this location instead of restricting to projects within the zip code
        newParams.zipOnly = true;
    } else if (params.cityName) {
        newParams.state = params.state;
        newParams.cityName = params.cityName;
    } else if (params.countyName) {
        newParams.state = params.state;
        newParams.countyName = params.countyName;
    } else if (params.state) {
        newParams.state = params.state;
    }

    return newParams;
};

export const removeLocationSpecificParamsForMapSearch = function (params) {
    const locationSpecificParams = {};
    if (params.centerZip) {
        locationSpecificParams.centerZip = params.centerZip;
        params.centerZip = null;
        params.zipOnly = false;
    }
    if (params.locationName) {
        locationSpecificParams.locationName = params.locationName;
        params.locationName = null;
    }
    if (params.cityName) {
        locationSpecificParams.cityName = params.cityName;
        params.cityName = null;
    }
    if (params.countyName) {
        locationSpecificParams.countyName = params.countyName;
        params.countyName = null;
    }
    if (params.state) {
        locationSpecificParams.state = params.state;
        params.state = null;
    }
    if (params.includeNearbyLocations) {
        params.includeNearbyLocations = false;
    }

    if (params.centerLat) {
        locationSpecificParams.centerLat = params.centerLat;
    }
    if (params.centerLng) {
        locationSpecificParams.centerLng = params.centerLng;
    }
    return locationSpecificParams;
};
